import * as React from "react";
import styled, { css } from "styled-components";
import moment from "moment-timezone";

import { Text } from "src/library/components/Text";
import {
  BasicModal,
  BasicModalProps
} from "src/library/components/Modal";
import { Link } from "src/library/components/Link";
import { formatPrice } from "src/core/prices/utils";
import { TransactionType } from "src/modules/payment/api/transactions.api";

import { PaymentTransaction } from "../../api/transactions.api";
import { transactionTypeToHuman } from "../../utils";
import { TransactionStatusText } from "../TransactionStatusText";

interface TransactionModalProps extends Omit<BasicModalProps, "title"> {
  transaction?: PaymentTransaction | null;
}

/**
 * Transaction
 */
const TransactionModal: React.ReactFCWithChildren<TransactionModalProps> = ({
  transaction,
  open,
  onDismiss
}) => (
  <StyledTransactionModal
    title={`Transaction #${transaction?.id}`}
    open={open}
    onDismiss={onDismiss}
  >
    {transaction && (
      <>
        <div className="row">
          <Text className="label">
            Order:
          </Text>

          <Link
            className="value"
            to={`/orders/${transaction.orderId}`}
          >
            {`#${transaction.orderId}`}
          </Link>
        </div>

        <div className="row">
          <Text className="label">
            Billing date:
          </Text>

          <Text className="value">
            {moment.utc(transaction.updatedAt).local().format("DD/MM/YYYY")}
          </Text>
        </div>

        <div className="row">
          <Text className="label">
            Type:
          </Text>

          <Text className="value">
            {transactionTypeToHuman(transaction.type)}
          </Text>
        </div>

        <div className="row">
          <Text className="label">
            Amount:
          </Text>

          <Text className="value">
            {`${transaction.type === TransactionType.CHARGE ? "-" : "+"} ${formatPrice(transaction.amount)}`}
          </Text>
        </div>

        <div className="row">
          <Text className="label">
            Status:
          </Text>

          <TransactionStatusText
            className="value"
            status={transaction.status}
          />
        </div>

        {transaction.errorReason && (
          <div className="row error">
            <Text className="label">
              {`Error reason (${transaction .errorReason.type}):`}
            </Text>

            <Text className="value">
              {transaction.errorReason.message}
            </Text>
          </div>
        )}
      </>
    )}
  </StyledTransactionModal>
);

export default TransactionModal;

const StyledTransactionModal = styled(BasicModal)`
  ${({ theme }) => css`
    .row {
      display: flex;
      align-items: center;
      padding-top: 0.4rem;

      &:first-of-type {
        padding-top: 0;
      }

      .label,
      .value {
        display: block;
      }

      .label {
        padding-right: 0.6rem;
        font-weight: ${theme.fonts.weights.bold};
      }

      a.value {
        transition: ${theme.transition.quickest};;
        color: ${theme.colors.primaryDark};

        &:hover {
          color: ${theme.colors.primaryDark};
        }
      }

      &.error {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  `};
`;
